import { Boundaries } from "@planzone/core";
import { organizationalUnitsFilter } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import {
  handleEditResourceRequest,
  handleEditResourceSuccess,
  handleEditResourceFailure,
  placeResources,
  handleAssignManagerToResourceSuccess,
  handleUnAssignManagerFromResourceSuccess,
  handleAssignEveryoneToManagerSuccess,
  handleSubordinatesRemoval,
  renameResourcesUnit,
  propagateResourceEvent,
} from "./resources";

const initialState: State = {
  organizationalUnits: [],
  filter: organizationalUnitsFilter,
  totalPages: 0,
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
};

function handleRequest(
  state: State,
  { filter }: Boundaries["retrieveOrganizationalUnits"]["request"],
): State {
  return filter.page === 0
    ? {
        organizationalUnits: [],
        request: setRequestStateToPending(),
        filter: { ...filter, page: filter.page + 1 },
        totalPages: 0,
      }
    : {
        ...state,
        request: setRequestStateToPending(),
        filter: { ...filter, page: filter.page + 1 },
      };
}

function handleSuccess(
  state: State,
  {
    organizationalUnits,
    filter,
    totalPages,
  }: Boundaries["retrieveOrganizationalUnits"]["success"],
): State {
  return {
    ...state,
    organizationalUnits:
      filter.page === 0
        ? organizationalUnits
        : [...state.organizationalUnits, ...organizationalUnits],
    request: setRequestStateToSuccess(),
    totalPages,
  };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleCreateOrganizationalUnitRequest(
  state: State,
  { organizationalUnit }: Boundaries["createOrganizationalUnit"]["request"],
): State {
  const { resources, ...pendingOrganizationalUnit } = organizationalUnit;

  // The back ranks a created unit after its siblings, and siblings are rendered in list order.
  return {
    ...state,
    organizationalUnits: [
      ...state.organizationalUnits,
      { ...pendingOrganizationalUnit, uuid: organizationalUnit.name },
    ],
  };
}

// The assigned resources leave the unit they were listed under to join the created one.
function handleCreateOrganizationalUnitSuccess(
  state: State,
  { organizationalUnit }: Boundaries["createOrganizationalUnit"]["success"],
): State {
  const { resources = [], ...createdOrganizationalUnit } = organizationalUnit;

  return placeResources(
    {
      ...state,
      organizationalUnits: state.organizationalUnits.map((unit) =>
        unit.uuid === organizationalUnit.name
          ? createdOrganizationalUnit
          : unit,
      ),
    },
    resources,
  );
}

function handleCreateOrganizationalUnitFailure(
  state: State,
  { organizationalUnit }: Boundaries["createOrganizationalUnit"]["failure"],
): State {
  return {
    ...state,
    organizationalUnits: state.organizationalUnits.filter(
      ({ uuid }) => uuid !== organizationalUnit.name,
    ),
  };
}

function handleEditOrganizationalUnitRequest(
  state: State,
  { organizationalUnit, name }: Boundaries["editOrganizationalUnit"]["request"],
): State {
  return renameResourcesUnit(
    {
      ...state,
      organizationalUnits: state.organizationalUnits.map((unit) =>
        unit.uuid === organizationalUnit.uuid ? { ...unit, name } : unit,
      ),
    },
    organizationalUnit.uuid,
    name,
  );
}

function handleEditOrganizationalUnitSuccess(
  state: State,
  { organizationalUnit }: Boundaries["editOrganizationalUnit"]["success"],
): State {
  // The PATCH response carries neither the embedded resources nor hasChildren.
  return renameResourcesUnit(
    {
      ...state,
      organizationalUnits: state.organizationalUnits.map((unit) =>
        unit.uuid === organizationalUnit.uuid
          ? { ...unit, ...organizationalUnit }
          : unit,
      ),
    },
    organizationalUnit.uuid,
    organizationalUnit.name,
  );
}

function handleUpdateOrganizationalUnitSuccess(
  state: State,
  success: Boundaries["updateOrganizationalUnit"]["success"],
): State {
  return placeResources(handleEditOrganizationalUnitSuccess(state, success), [
    ...success.assignedResources,
    ...success.unassignedResources,
  ]);
}

function handleEditOrganizationalUnitFailure(
  state: State,
  { organizationalUnit }: Boundaries["editOrganizationalUnit"]["failure"],
): State {
  return renameResourcesUnit(
    {
      ...state,
      organizationalUnits: state.organizationalUnits.map((unit) =>
        unit.uuid === organizationalUnit.uuid ? organizationalUnit : unit,
      ),
    },
    organizationalUnit.uuid,
    organizationalUnit.name,
  );
}

function handleRemoveOrganizationalUnitRequest(
  state: State,
  organizationalUnit: Boundaries["removeOrganizationalUnit"]["request"],
): State {
  return {
    ...state,
    organizationalUnits: state.organizationalUnits.filter(
      ({ uuid }) => uuid !== organizationalUnit.uuid,
    ),
  };
}

function handleRemoveOrganizationalUnitSuccess(
  state: State,
  {
    organizationalUnitUuids,
  }: Boundaries["removeOrganizationalUnit"]["success"],
): State {
  return {
    ...state,
    organizationalUnits: state.organizationalUnits.filter(
      ({ uuid }) => !organizationalUnitUuids.includes(uuid),
    ),
  };
}

function handleRemoveOrganizationalUnitFailure(
  state: State,
  { organizationalUnit }: Boundaries["removeOrganizationalUnit"]["failure"],
): State {
  return state.organizationalUnits.some(
    ({ uuid }) => uuid === organizationalUnit.uuid,
  )
    ? state
    : {
        ...state,
        organizationalUnits: [...state.organizationalUnits, organizationalUnit],
      };
}

// Siblings are rendered in list order, so the moved unit is placed right after `insertAfterUuid`,
// or at the end when there is none, mirroring where the back ranks it.
function placeMovedOrganizationalUnit(
  organizationalUnits: State["organizationalUnits"],
  {
    organizationalUnit,
    newParentUuid,
    insertAfterUuid,
  }: Boundaries["moveOrganizationalUnit"]["request"],
): State["organizationalUnits"] {
  const others = organizationalUnits.filter(
    ({ uuid }) => uuid !== organizationalUnit.uuid,
  );
  const movedOrganizationalUnit = {
    ...organizationalUnit,
    parentUuid: newParentUuid,
  };
  const index = insertAfterUuid
    ? others.findIndex(({ uuid }) => uuid === insertAfterUuid)
    : -1;

  return index === -1
    ? [...others, movedOrganizationalUnit]
    : [
        ...others.slice(0, index + 1),
        movedOrganizationalUnit,
        ...others.slice(index + 1),
      ];
}

function handleMoveOrganizationalUnitRequest(
  state: State,
  request: Boundaries["moveOrganizationalUnit"]["request"],
): State {
  return {
    ...state,
    organizationalUnits: placeMovedOrganizationalUnit(
      state.organizationalUnits,
      request,
    ),
  };
}

function handleMoveOrganizationalUnitSuccess(
  state: State,
  success: Boundaries["moveOrganizationalUnit"]["success"],
): State {
  return {
    ...state,
    organizationalUnits: placeMovedOrganizationalUnit(
      state.organizationalUnits,
      success,
    ),
  };
}

function handleMoveOrganizationalUnitFailure(
  state: State,
  { organizationalUnit }: Boundaries["moveOrganizationalUnit"]["failure"],
): State {
  return {
    ...state,
    organizationalUnits: [
      ...state.organizationalUnits.filter(
        ({ uuid }) => uuid !== organizationalUnit.uuid,
      ),
      organizationalUnit,
    ],
  };
}

function propagateEvent(state = initialState, event: Event): State {
  return propagateResourceEvent(state, event);
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveOrganizationalUnitsRequested]: handleRequest,
      [events.retrieveOrganizationalUnitsSucceeded]: handleSuccess,
      [events.retrieveOrganizationalUnitsFailed]: handleFailure,
      [events.createOrganizationalUnitRequested]:
        handleCreateOrganizationalUnitRequest,
      [events.createOrganizationalUnitSucceeded]:
        handleCreateOrganizationalUnitSuccess,
      [events.createOrganizationalUnitFailed]:
        handleCreateOrganizationalUnitFailure,
      [events.editOrganizationalUnitRequested]:
        handleEditOrganizationalUnitRequest,
      [events.editOrganizationalUnitSucceeded]:
        handleEditOrganizationalUnitSuccess,
      [events.editOrganizationalUnitFailed]:
        handleEditOrganizationalUnitFailure,
      [events.removeOrganizationalUnitRequested]:
        handleRemoveOrganizationalUnitRequest,
      [events.removeOrganizationalUnitSucceeded]:
        handleRemoveOrganizationalUnitSuccess,
      [events.removeOrganizationalUnitFailed]:
        handleRemoveOrganizationalUnitFailure,
      [events.moveOrganizationalUnitRequested]:
        handleMoveOrganizationalUnitRequest,
      [events.moveOrganizationalUnitSucceeded]:
        handleMoveOrganizationalUnitSuccess,
      [events.moveOrganizationalUnitFailed]:
        handleMoveOrganizationalUnitFailure,
      [events.editResourceRequested]: handleEditResourceRequest,
      [events.editResourceSucceeded]: handleEditResourceSuccess,
      [events.editResourceFailed]: handleEditResourceFailure,
      [events.updateOrganizationalUnitSucceeded]:
        handleUpdateOrganizationalUnitSuccess,
      [events.assignManagerToResourceSucceeded]:
        handleAssignManagerToResourceSuccess,
      [events.unAssignManagerFromResourceSucceeded]:
        handleUnAssignManagerFromResourceSuccess,
      [events.assignEveryoneToManagerSucceeded]:
        handleAssignEveryoneToManagerSuccess,
      [events.removeSubordinatesSucceeded]: handleSubordinatesRemoval,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
